import React from "react";
import { JointPositionSliderOverlay } from "./scene-overlays/JointPositionSliderOverlay";
import { SceneContextMenu } from "./scene-overlays/SceneContextMenu";
import { SelectedLayerVisibilityNotice } from "./scene-overlays/SelectedLayerVisibilityNotice";
import { SHORTCUT_ACTIONS } from "../shortcuts/defaultBindings";
import { useShortcutAction } from "../shortcuts/useShortcutAction";

export function SceneViewportLayout({
  actuatorSliderState,
  contextMenuProps,
  handleSceneContextMenuCapture,
  handleSelectedLayerVisibilityOnClick,
  jointPositionSliderOverlay,
  mobileViewportBlankTapHandlers,
  selectedJoint,
  selectedLayerVisibilityNotice,
  setMountNodeRef,
}) {
  const viewportRef = React.useRef(null);
  const setViewportNodeRef = React.useCallback(
    (node) => {
      viewportRef.current = node;
      setMountNodeRef(node);
    },
    [setMountNodeRef]
  );

  const openContextMenuFromKeyboard = React.useCallback(
    (event) => {
      const viewport = viewportRef.current;
      if (!viewport || document.activeElement !== viewport) return false;
      const rect = viewport.getBoundingClientRect();
      const clientX = rect.left + rect.width / 2;
      const clientY = rect.top + rect.height / 2;
      handleSceneContextMenuCapture({
        clientX,
        clientY,
        preventDefault: () => event?.preventDefault?.(),
        stopPropagation: () => event?.stopPropagation?.(),
      });
      return true;
    },
    [handleSceneContextMenuCapture]
  );
  const contextMenuShortcutOptions = React.useMemo(
    () => ({
      priority: 20,
      stopPropagation: true,
    }),
    []
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.SCENE_CONTEXT_MENU_OPEN,
    openContextMenuFromKeyboard,
    contextMenuShortcutOptions
  );

  return (
    <>
      <div
        ref={setViewportNodeRef}
        role="application"
        aria-label="3D robot editor viewport"
        aria-keyshortcuts="Shift+F10"
        tabIndex={0}
        style={{ width: "100%", height: "100%" }}
        onContextMenuCapture={handleSceneContextMenuCapture}
        onPointerDownCapture={mobileViewportBlankTapHandlers.onPointerDownCapture}
        onPointerUpCapture={mobileViewportBlankTapHandlers.onPointerUpCapture}
        onPointerCancelCapture={mobileViewportBlankTapHandlers.onPointerCancelCapture}
      />
      <SelectedLayerVisibilityNotice
        notice={selectedLayerVisibilityNotice}
        onTurnVisibilityOn={handleSelectedLayerVisibilityOnClick}
      />
      <JointPositionSliderOverlay
        actuatorSliderState={actuatorSliderState}
        isDragging={jointPositionSliderOverlay.isDragging}
        onBlur={jointPositionSliderOverlay.onBlur}
        onPointerCancel={jointPositionSliderOverlay.onPointerCancel}
        onPointerDown={jointPositionSliderOverlay.onPointerDown}
        onPointerMove={jointPositionSliderOverlay.onPointerMove}
        onPointerUp={jointPositionSliderOverlay.onPointerUp}
        onStopEvent={jointPositionSliderOverlay.onStopEvent}
        onValueChange={jointPositionSliderOverlay.onValueChange}
        position={jointPositionSliderOverlay.position}
        range={jointPositionSliderOverlay.range}
        selectedJoint={selectedJoint}
        selectedJointDef={jointPositionSliderOverlay.selectedJointDef}
        step={jointPositionSliderOverlay.step}
        value={jointPositionSliderOverlay.value}
        visible={jointPositionSliderOverlay.visible}
      />
      <SceneContextMenu {...contextMenuProps} />
    </>
  );
}
